import { useMemo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useControlledField, TextField, useDebouncedValue } from '@frontend/design-system';
import { useUserSearch } from '../../../hooks/use-user-search';
import { getUserFullName } from '../../../utils';
import { UserRow } from '../../common/user-row/user-row';
import { UsersSkeletonLoader } from '../../common/users-skeleton-loader/users-skeleton-loader';

interface Props {
  selectedMemberIds: string[];
  existingMemberIds: string[];
  onChange: (userIds: string[]) => void;
  show: boolean;
}

export const AddMembers = ({ selectedMemberIds, existingMemberIds, onChange, show }: Props) => {
  const { t } = useTranslation('team-chat');
  const { onChangeSearchTerm, searchTerm, isLoading, usersToDisplay } = useUserSearch();

  const textFieldRef = useRef<HTMLDivElement>(null);

  const textFieldProps = useControlledField({
    value: searchTerm,
    type: 'text',
    onChange: onChangeSearchTerm,
  });

  const searchText = useDebouncedValue(searchTerm);

  const searchUsers = useMemo(() => {
    const input = searchText.trim().toLowerCase();
    const nonMembers = usersToDisplay.filter((user) => !existingMemberIds.includes(user.userID));

    // The users are filtered using the members that are already added to the channel
    // When a member is added, we do not want to filter the user out
    // Instead, we want to show the user with an added Text
    if (!input) {
      return nonMembers;
    } else {
      return nonMembers?.filter((user) => {
        const fullName = getUserFullName(user.firstName, user.lastName).toLowerCase();
        return fullName.includes(input);
      });
    }
  }, [searchText, usersToDisplay, existingMemberIds]);

  const addMember = async (userId: string) => onChange([...selectedMemberIds, userId]);
  const removeUser = (userId: string) => onChange(selectedMemberIds.filter((id) => id !== userId));

  useEffect(() => {
    if (show && textFieldRef.current) {
      /*
        Modal body get focused when the modalProps.show property is set to true. Hence, when we focus on the text field,
        it get overridden by the modal body focus. To avoid this, we are setting a timeout of 600ms to focus on
        the text field.
       */
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 600);
    }
  }, [show]);

  return (
    <>
      <TextField
        name=''
        label=''
        startAdornment={<Icon name='search' />}
        placeholder={t('Search members')}
        autoFocus
        clearable
        fieldComponentProps={{ ref: textFieldRef }}
        {...textFieldProps}
      />
      <div css={userListContainerStyle}>
        <PaginatedList
          listData={searchUsers}
          emptyStateConfig={{
            header: t('No members found'),
            type: 'generic',
          }}
          skeletonLoader={<UsersSkeletonLoader />}
          isLoading={isLoading}
          height={350}
          renderListItem={({ listItem: user }) => {
            return (
              <UserRow
                key={user.userID}
                user={user}
                selectedMemberIds={selectedMemberIds}
                onRemoveUser={removeUser}
                onAddUser={addMember}
              />
            );
          }}
        />
      </div>
    </>
  );
};

const userListContainerStyle = css({
  overflowY: 'scroll',
  overflowX: 'hidden',
  boxShadow: theme.shadows.light,
  borderBottomLeftRadius: theme.borderRadius.medium,
  borderBottomRightRadius: theme.borderRadius.medium,
  marginBottom: theme.spacing(1),
});
